import {createAsyncThunk, createSlice, PayloadAction} from '@reduxjs/toolkit';
import {RootState} from "../../../app/store";
import {
    ConfigurationRegion,
    FullLocationUi,
    LocationInListDTO,
    UpdateLocationRequest,
    UpdateWorkingHoursRequest,
    UpdateWorkScheduleRequest,
    WorkingHoursUi,
    WorkScheduleUi
} from "../../../api/generated";
import {WorkingHoursRequest} from "./modal/createAndUpdate/LocationForm";
import {locationApi, RequestStatus} from "../../../api/apiConfiguration";
import {selectExceptionally} from "../../../utils/selectExceptionally";
import {prepareEmails} from "../assistant/botCreateSlice";
import {fetchManufacturerVehicleTypes, VehicleTypesByManufacturer} from "./manufacturerVehicleTypesSlice";

export const fetchLocationToUpdate = createAsyncThunk(
    'locationUpdate/fetchLocationById',
    (publicId: string) => locationApi.getLocationById({publicId})
);

export const updateLocation = createAsyncThunk<LocationInListDTO, undefined, { state: RootState }>(
    "locationUpdate/update",
    async (_payload, {getState, rejectWithValue}) => {
        const {locationUpdate} = getState();
        const {updateLocationRequest} = locationUpdate;
        const request = mapRequest(updateLocationRequest);
        try {
            return await locationApi.updateLocation({updateLocationRequest: request});
        } catch (error: any) {
            const message = error?.response ? await error.response.text() : null;
            return rejectWithValue(message);
        }
    }
);

export type UpdateLocationRequestUI =
    Omit<UpdateLocationRequest, "locationEmailTranscriptRecipients" | "aftersalesEmailTranscriptRecipients">
    & {
    locationEmailTranscriptRecipients: string;
    aftersalesEmailTranscriptRecipients: string;
}
export type UpdateLocationRequestPartial = Partial<UpdateLocationRequestUI>

export interface UpdateLocationState {
    updateLocationRequest?: UpdateLocationRequestPartial
    initialLocationLoadingStatus: RequestStatus
    updateLocationRequestStatus: RequestStatus
    errorMessage: string | null
}

const buildInitialState = (): UpdateLocationState => {
    return {
        initialLocationLoadingStatus: RequestStatus.NONE,
        updateLocationRequestStatus: RequestStatus.NONE,
        errorMessage: null
    }
}

const initialState: UpdateLocationState = buildInitialState()

export const locationUpdateSlice = createSlice({
    name: 'locationUpdate',
    initialState,
    reducers: {
        clearUpdateLocationState: () => {
            return buildInitialState();
        },
        updateWorkingHours: (state, {payload}: PayloadAction<WorkingHoursRequest & { fieldName: string }>) => {
            if (state.updateLocationRequest && state.updateLocationRequest.workSchedule && state.updateLocationRequest.workSchedule[payload.fieldName as keyof typeof state.updateLocationRequest.workSchedule]) {
                // @ts-ignore
                state.updateLocationRequest.workSchedule[payload.fieldName as keyof typeof state.updateLocationRequest.workSchedule]!.openTime = payload.openTime
                // @ts-ignore
                state.updateLocationRequest.workSchedule[payload.fieldName as keyof typeof state.updateLocationRequest.workSchedule]!.closeTime = payload.closeTime
                // @ts-ignore
                state.updateLocationRequest.workSchedule[payload.fieldName as keyof typeof state.updateLocationRequest.workSchedule]!.isClosed = payload.isClosed
            }
        },
        updateAftersalesWorkingHours: (state, {payload}: PayloadAction<WorkingHoursRequest & {
            fieldName: string
        }>) => {
            if (state.updateLocationRequest && state.updateLocationRequest.aftersalesWorkSchedule && state.updateLocationRequest.aftersalesWorkSchedule[payload.fieldName as keyof typeof state.updateLocationRequest.aftersalesWorkSchedule]) {
                // @ts-ignore
                state.updateLocationRequest.aftersalesWorkSchedule[payload.fieldName as keyof typeof state.updateLocationRequest.aftersalesWorkSchedule]!.openTime = payload.openTime
                // @ts-ignore
                state.updateLocationRequest.aftersalesWorkSchedule[payload.fieldName as keyof typeof state.updateLocationRequest.aftersalesWorkSchedule]!.closeTime = payload.closeTime
                // @ts-ignore
                state.updateLocationRequest.aftersalesWorkSchedule[payload.fieldName as keyof typeof state.updateLocationRequest.aftersalesWorkSchedule]!.isClosed = payload.isClosed
            }
        },
        updateUpdateLocationRequest: (state, {payload}: PayloadAction<UpdateLocationRequestPartial>) => {
            state.updateLocationRequest = {
                ...state.updateLocationRequest,
                ...payload
            }
        }
    },
    extraReducers: (builder) => {
        builder.addCase(fetchLocationToUpdate.pending, (state) => {
            state.initialLocationLoadingStatus = RequestStatus.PENDING;
        })
        builder.addCase(fetchLocationToUpdate.fulfilled, (state, {payload}) => {
            state.initialLocationLoadingStatus = RequestStatus.RESOLVED;
            state.updateLocationRequest = assignFields(payload);
        })
        builder.addCase(fetchLocationToUpdate.rejected, (state) => {
            state.initialLocationLoadingStatus = RequestStatus.REJECTED;
        })
        builder.addCase(updateLocation.pending, (state) => {
            state.updateLocationRequestStatus = RequestStatus.PENDING;
            state.errorMessage = null;
        })
        builder.addCase(updateLocation.fulfilled, (state) => {
            state.updateLocationRequestStatus = RequestStatus.RESOLVED;
        })
        builder.addCase(updateLocation.rejected, (state, action) => {
            state.updateLocationRequestStatus = RequestStatus.REJECTED;
            state.errorMessage = (action.payload as string | null) ?? null;
        })
        builder.addCase(fetchManufacturerVehicleTypes.fulfilled, (state, {payload: catalog}) => {
            const supported = state.updateLocationRequest?.supportedVehicleManufacturers;
            if (supported) {
                state.updateLocationRequest!.supportedVehicleManufacturers = withCatalogVehicleTypes(supported, catalog);
            }
        })
    },
});

// Migrated manufacturers are saved without vehicle types; give them the ones the catalog lists.
export const withCatalogVehicleTypes = (supported: { [manufacturer: string]: string[] },
                                        catalog: VehicleTypesByManufacturer) =>
    Object.fromEntries(Object.entries(supported).map(([manufacturer, vehicleTypes]) =>
        [manufacturer, vehicleTypes.length === 0 && catalog[manufacturer] ? catalog[manufacturer] : vehicleTypes]));

const mapWorkingHours = (workingHours: WorkingHoursUi): UpdateWorkingHoursRequest => {
    const [openTimeHour, openTimeMinute] = String(workingHours.openTime).split(":")
    const [closeTimeHour, closeTimeMinute] = String(workingHours.closeTime).split(":")
    return {
        id: workingHours.id,
        openTime: {
            hour: Number(openTimeHour),
            minute: Number(openTimeMinute),
        },
        closeTime: {
            hour: Number(closeTimeHour),
            minute: Number(closeTimeMinute),
        },
        isClosed: workingHours.isClosed
    }
}

const mapWorkSchedule = (workSchedule: WorkScheduleUi): UpdateWorkScheduleRequest => {
    return {
        id: workSchedule.id,
        monday: mapWorkingHours(workSchedule.monday),
        tuesday: mapWorkingHours(workSchedule.tuesday),
        wednesday: mapWorkingHours(workSchedule.wednesday),
        thursday: mapWorkingHours(workSchedule.thursday),
        friday: mapWorkingHours(workSchedule.friday),
        saturday: mapWorkingHours(workSchedule.saturday),
        sunday: mapWorkingHours(workSchedule.sunday)
    }
}

const assignFields = (locationUi: FullLocationUi): UpdateLocationRequestPartial => {
    return {
        id: locationUi.id,
        publicId: locationUi.publicId,
        name: locationUi.name,
        city: locationUi.city,
        postcode: locationUi.postcode,
        phoneNumber: locationUi.phoneNumber,
        workSchedule: mapWorkSchedule(locationUi.workSchedule),
        vehicleDataConfigurationId: locationUi.vehicleDataConfigurationId,
        vehicleDataProvider: locationUi.vehicleDataProvider,
        advertiserId: locationUi.advertiserId,
        filePath: locationUi.filePath,
        feedId: locationUi.feedId,
        locationHash: locationUi.locationHash,
        locationEmailConfigurationId: locationUi.locationEmailConfigurationId,
        locationEmailTranscriptRecipients: (locationUi.locationEmailTranscriptRecipients || []).join(";"),
        locationEmailFiltrationEnabled: locationUi.locationEmailFiltrationEnabled,
        locationEmailTranscriptSummaryEnabled: Boolean(locationUi.locationEmailTranscriptSummaryEnabled),
        enquiryConfigurationId: locationUi.enquiryConfigurationId,
        enquiryProvider: locationUi.enquiryProvider,
        dragon2000DealerId: locationUi.dragon2000DealerId,
        em2Region: locationUi.em2Region ?? ConfigurationRegion.Uk,
        aftersalesConfigurationId: locationUi.aftersalesConfigurationId,
        aftersalesEnabled: locationUi.aftersalesEnabled,
        serviceBookingUrl: locationUi.serviceBookingUrl,
        recallUrl: locationUi.recallUrl,
        aftersalesEmailTranscriptRecipients: locationUi.aftersalesEmailTranscriptRecipients.join(";"),
        aftersalesEmailFiltrationEnabled: locationUi.aftersalesEmailFiltrationEnabled,
        aftersalesEmailTranscriptSummaryEnabled: Boolean(locationUi.aftersalesEmailTranscriptSummaryEnabled),
        aftersalesWorkSchedule: mapWorkSchedule(locationUi.aftersalesWorkSchedule),
        supportedVehicleManufacturers: locationUi.supportedVehicleManufacturers || {},
    }
}

const mapRequest = (request?: UpdateLocationRequestPartial): UpdateLocationRequest => {
    if (request) {
        const mappedRequest = {
            id: selectExceptionally(request.id),
            publicId: selectExceptionally(request.publicId),
            name: selectExceptionally(request.name),
            city: selectExceptionally(request.city),
            postcode: selectExceptionally(request.postcode),
            phoneNumber: selectExceptionally(request.phoneNumber),
            workSchedule: selectExceptionally(request.workSchedule),
            vehicleDataConfigurationId: selectExceptionally(request.vehicleDataConfigurationId),
            vehicleDataProvider: selectExceptionally(request.vehicleDataProvider),
            advertiserId: request.advertiserId,
            filePath: request.filePath,
            feedId: request.feedId,
            locationHash: request.locationHash,
            locationEmailConfigurationId: request.locationEmailConfigurationId,
            locationEmailTranscriptRecipients: prepareEmails(request.locationEmailTranscriptRecipients),
            locationEmailFiltrationEnabled: selectExceptionally(request.locationEmailFiltrationEnabled),
            locationEmailTranscriptSummaryEnabled: Boolean(request.locationEmailTranscriptSummaryEnabled),
            enquiryConfigurationId: selectExceptionally(request.enquiryConfigurationId),
            enquiryProvider: selectExceptionally(request.enquiryProvider),
            dragon2000DealerId: request.dragon2000DealerId,
            em2Region: request.em2Region,
            aftersalesConfigurationId: selectExceptionally(request.aftersalesConfigurationId),
            aftersalesEnabled: selectExceptionally(request.aftersalesEnabled),
            serviceBookingUrl: request.aftersalesEnabled ? selectExceptionally(request.serviceBookingUrl) : request.serviceBookingUrl,
            recallUrl: request.recallUrl || undefined,
            aftersalesEmailTranscriptRecipients: prepareEmails(request.aftersalesEmailTranscriptRecipients),
            aftersalesEmailFiltrationEnabled: selectExceptionally(request.aftersalesEmailFiltrationEnabled),
            aftersalesEmailTranscriptSummaryEnabled: Boolean(request.aftersalesEmailTranscriptSummaryEnabled),
            aftersalesWorkSchedule: selectExceptionally(request.aftersalesWorkSchedule),
            supportedVehicleManufacturers: request.supportedVehicleManufacturers || {},
        } as UpdateLocationRequest;
        return mappedRequest as UpdateLocationRequest;
    } else {
        throw new Error("update location request not initialised")
    }
}

export const {
    updateWorkingHours,
    updateAftersalesWorkingHours,
    clearUpdateLocationState,
    updateUpdateLocationRequest
} = locationUpdateSlice.actions;

export const updateLocationRequestSelector = (state: RootState) => state.locationUpdate.updateLocationRequest as UpdateLocationRequestPartial;
export const initialLocationLoadingStatusSelector = (state: RootState): RequestStatus => state.locationUpdate.initialLocationLoadingStatus;
export const updateLocationRequestStatusSelector = (state: RootState): RequestStatus => state.locationUpdate.updateLocationRequestStatus;
export const updateLocationErrorMessageSelector = (state: RootState): string | null => state.locationUpdate.errorMessage;

export const mapFullLocationToUpdateRequest = (locationUi: FullLocationUi): UpdateLocationRequest => mapRequest(assignFields(locationUi));
